import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useEvents } from './events';

const STORAGE_KEY = 'gw2-metas-settings-v1';
const COMPLETIONS_KEY = 'gw2-metas-completions-v1';
const SECTIONS_KEY = 'gw2-metas-sections-v1';
const FAVORITES_KEY = 'gw2-metas-favorites-v1';
const SCHEDULE_LOOKAHEAD_KEY = 'gw2-metas-schedule-lookahead-v1';
const DAY_NIGHT_TRACKERS_KEY = 'gw2-metas-day-night-trackers-v1';
const SETTINGS_DRAWER_KEY = 'gw2-metas-settings-drawer-open-v1';
const SHOW_LOCATIONS_KEY = 'gw2-metas-show-locations-v1';
const SHOW_FAVORITES_KEY = 'gw2-metas-show-favorites-v1';
const SHOW_DONE_KEY = 'gw2-metas-show-done-v1';

export interface SettingsState {
  trackedIds: string[];
  toggleTracked: (id: string) => void;
  isTracked: (id: string) => boolean;
  completionsToday: Record<string, string>; // id -> ISO date (yyyy-mm-dd)
  toggleCompletedToday: (id: string) => void;
  isCompletedToday: (id: string) => boolean;
  sectionsCollapsed: { current: boolean; upcoming: boolean };
  toggleSectionCollapsed: (section: 'current' | 'upcoming') => void;
  favorites: string[];
  toggleFavorite: (id: string) => void;
  isFavorite: (id: string) => boolean;
  unfavoriteAll: () => void;
  scheduleLookaheadHours: number; // How many hours ahead to show schedule
  setScheduleLookaheadHours: (hours: number) => void;
  dayNightTrackers: { tyria: boolean; cantha: boolean };
  toggleDayNightTracker: (tracker: 'tyria' | 'cantha') => void;
  settingsDrawerOpen: boolean;
  setSettingsDrawerOpen: (open: boolean) => void;
  showLocations: boolean;
  toggleShowLocations: () => void;
  showFavorites: boolean;
  toggleShowFavorites: () => void;
  showDone: boolean;
  toggleShowDone: () => void;
}

const SettingsContext = createContext<SettingsState | null>(null);

function getTodayISO(now: Date = new Date()) {
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { events: META_EVENTS } = useEvents();
  
  const [trackedIds, setTrackedIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // If parsed value is an array and has items, use it
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
      // If no saved settings or empty array, will be set to all event IDs once events are loaded
      return [];
    } catch {
      return [];
    }
  });
  
  // Update trackedIds to include all events when events load for the first time (if no saved settings)
  useEffect(() => {
    if (META_EVENTS.length > 0 && trackedIds.length === 0) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
          // No saved settings - default to all event IDs
          setTrackedIds(META_EVENTS.map(e => e.id));
        } else {
          const parsed = JSON.parse(raw);
          // If empty array or invalid data, default to all event IDs (treat empty as "no preference")
          if (!Array.isArray(parsed) || parsed.length === 0) {
            setTrackedIds(META_EVENTS.map(e => e.id));
          }
        }
      } catch {
        // If error reading, default to all event IDs
        setTrackedIds(META_EVENTS.map(e => e.id));
      }
    }
  }, [META_EVENTS.length, trackedIds.length]);

  const [completionsToday, setCompletionsToday] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem(COMPLETIONS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [sectionsCollapsed, setSectionsCollapsed] = useState<{ current: boolean; upcoming: boolean }>(() => {
    try {
      const raw = localStorage.getItem(SECTIONS_KEY);
      return raw ? JSON.parse(raw) : { current: false, upcoming: false };
    } catch {
      return { current: false, upcoming: false };
    }
  });

  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [scheduleLookaheadHours, setScheduleLookaheadHoursState] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(SCHEDULE_LOOKAHEAD_KEY);
      return raw ? JSON.parse(raw) : 8; // Default to 8 hours
    } catch {
      return 24;
    }
  });

  const [dayNightTrackers, setDayNightTrackersState] = useState<{ tyria: boolean; cantha: boolean }>(() => {
    try {
      const raw = localStorage.getItem(DAY_NIGHT_TRACKERS_KEY);
      return raw ? JSON.parse(raw) : { tyria: false, cantha: false };
    } catch {
      return { tyria: false, cantha: false };
    }
  });

  const [settingsDrawerOpen, setSettingsDrawerOpenState] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(SETTINGS_DRAWER_KEY);
      return raw ? JSON.parse(raw) : false;
    } catch {
      return false;
    }
  });

  const [showLocations, setShowLocationsState] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(SHOW_LOCATIONS_KEY);
      return raw ? JSON.parse(raw) : false; // Default to showing locations
    } catch {
      return true;
    }
  });

  const [showFavorites, setShowFavoritesState] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(SHOW_FAVORITES_KEY);
      return raw ? JSON.parse(raw) : false; // Default to hiding favorites
    } catch {
      return false;
    }
  });

  const [showDone, setShowDoneState] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(SHOW_DONE_KEY);
      return raw ? JSON.parse(raw) : true; // Default to showing done
    } catch {
      return true;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trackedIds));
  }, [trackedIds]);

  useEffect(() => {
    localStorage.setItem(COMPLETIONS_KEY, JSON.stringify(completionsToday));
  }, [completionsToday]);

  useEffect(() => {
    localStorage.setItem(SECTIONS_KEY, JSON.stringify(sectionsCollapsed));
  }, [sectionsCollapsed]);

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(SCHEDULE_LOOKAHEAD_KEY, JSON.stringify(scheduleLookaheadHours));
  }, [scheduleLookaheadHours]);

  useEffect(() => {
    localStorage.setItem(DAY_NIGHT_TRACKERS_KEY, JSON.stringify(dayNightTrackers));
  }, [dayNightTrackers]);

  useEffect(() => {
    localStorage.setItem(SETTINGS_DRAWER_KEY, JSON.stringify(settingsDrawerOpen));
  }, [settingsDrawerOpen]);

  useEffect(() => {
    localStorage.setItem(SHOW_LOCATIONS_KEY, JSON.stringify(showLocations));
  }, [showLocations]);

  useEffect(() => {
    localStorage.setItem(SHOW_FAVORITES_KEY, JSON.stringify(showFavorites));
  }, [showFavorites]);

  useEffect(() => {
    localStorage.setItem(SHOW_DONE_KEY, JSON.stringify(showDone));
  }, [showDone]);

  // Helper function to get all event IDs in the same group
  const getEventGroupIds = useMemo(() => {
    return (eventId: string): string[] => {
      const event = META_EVENTS.find(e => e.id === eventId);
      if (!event || !event.eventGroup) {
        return [eventId];
      }
      // Return all event IDs that share the same eventGroup
      return META_EVENTS
        .filter(e => e.eventGroup === event.eventGroup)
        .map(e => e.id);
    };
  }, [META_EVENTS]);

  const api = useMemo<SettingsState>(() => ({
    trackedIds,
    toggleTracked: (id: string) => {
      setTrackedIds(prev => {
        const groupIds = getEventGroupIds(id);
        const isCurrentlyTracked = groupIds.some(groupId => prev.includes(groupId));
        
        if (isCurrentlyTracked) {
          // Remove all events in the group
          return prev.filter(x => !groupIds.includes(x));
        } else {
          // Add all events in the group
          const newIds = groupIds.filter(groupId => !prev.includes(groupId));
          return [...prev, ...newIds];
        }
      });
    },
    isTracked: (id: string) => {
      // For grouped events, check if any event in the group is tracked
      const groupIds = getEventGroupIds(id);
      return groupIds.some(groupId => trackedIds.includes(groupId));
    },
    completionsToday,
    toggleCompletedToday: (id: string) => {
      setCompletionsToday(prev => {
        const today = getTodayISO();
        const current = prev[id];
        const next = { ...prev };
        if (current === today) {
          delete next[id];
        } else {
          next[id] = today;
        }
        return next;
      });
    },
    isCompletedToday: (id: string) => completionsToday[id] === getTodayISO(),
    sectionsCollapsed,
    toggleSectionCollapsed: (section: 'current' | 'upcoming') => {
      setSectionsCollapsed(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    },
    favorites,
    toggleFavorite: (id: string) => {
      setFavorites(prev => {
        return prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      });
    },
    isFavorite: (id: string) => favorites.includes(id),
    unfavoriteAll: () => {
      setFavorites([]);
    },
    scheduleLookaheadHours,
    setScheduleLookaheadHours: (hours: number) => {
      setScheduleLookaheadHoursState(hours);
    },
    dayNightTrackers,
    toggleDayNightTracker: (tracker: 'tyria' | 'cantha') => {
      setDayNightTrackersState(prev => ({
        ...prev,
        [tracker]: !prev[tracker]
      }));
    },
    settingsDrawerOpen,
    setSettingsDrawerOpen: (open: boolean) => {
      setSettingsDrawerOpenState(open);
    },
    showLocations,
    toggleShowLocations: () => {
      setShowLocationsState(prev => !prev);
    },
    showFavorites,
    toggleShowFavorites: () => {
      setShowFavoritesState(prev => !prev);
    },
    showDone,
    toggleShowDone: () => {
      setShowDoneState(prev => !prev);
    },
  }), [trackedIds, completionsToday, sectionsCollapsed, favorites, scheduleLookaheadHours, dayNightTrackers, settingsDrawerOpen, showLocations, showFavorites, showDone, getEventGroupIds]);

  return <SettingsContext.Provider value={api}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}


